import { computed, DestroyRef, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { filter } from 'rxjs';
import { UserInfo } from './user-info';
import { SignalStoreResetRegistry } from '../../../modules/shared/signal-store';

type UserInfoState = {
  data: UserInfo | null;
  loaded: boolean;
  loading: boolean;
};

export const UserInfoStore = signalStore(
  { providedIn: 'root' },
  withState<UserInfoState>({ data: null, loaded: false, loading: false }),
  withProps((store) => ({
    language$: toObservable(computed(() => store.data()?.language)),
    theme$: toObservable(computed(() => store.data()?.theme)),
    allowCurrentDay$: toObservable(computed(() => store.data()?.allowCurrentDay)),
    isAdmin$: toObservable(computed(() => store.data()?.adminAccess)),
    data$: toObservable(store.data),
    select$: toObservable(store.data).pipe(filter((x): x is UserInfo => x != null)),
    loading$: toObservable(store.loading),
    hasCache$: toObservable(store.loaded),
  })),
  withMethods((store) => ({
    set: (info: UserInfo) => patchState(store, { data: info, loaded: true }),
    updateData: (partial: Partial<UserInfo>) =>
      patchState(store, state => ({
        data: state.data ? { ...state.data, ...partial } : null
      })),
    setLoading: (loading: boolean) => patchState(store, { loading }),
    resetCache: () => patchState(store, { loaded: false }),
    reset: () => patchState(store, { data: null, loaded: false, loading: false })
  })),
  withHooks({
    onInit(store) {
      const registry = inject(SignalStoreResetRegistry);
      const unregister = registry.register(() => store.reset());
      inject(DestroyRef).onDestroy(unregister);
    },
  })
);
